import React, { useState, useEffect, useMemo } from 'react'
import { useAuth } from '@/hooks/useAuth'
import ProtectedRoute from '@/components/ProtectedRoute'
import { MetricCard } from '@/components/MetricCard'
import { getAllMonumentaEmployees, getAllCourses } from '@/lib/real-data-service'
import { 
  DollarSign,
  TrendingUp,
  BookOpen,
  Users,
  Calendar,
  Target,
  AlertTriangle,
  CheckCircle,
  Clock,
  PieChart,
  Filter,
  Download,
  Plus
} from 'lucide-react'
import { usePageTitle } from '@/stores/page-title-store'

// Budget summary data
const BUDGET_DATA = {
  totalBudget: 120000,
  usedBudget: 75000,
  availableBudget: 45000,
  coursesPlanned: 15,
  coursesCompleted: 8,
  coursesInProgress: 4,
  employeesEnrolled: 32,
  averageCostPerCourse: 9375,
  budgetUtilization: 62.5,
  nextQuarterBudget: 30000
}

// Generate course categories from real data
const generateCourseCategories = (allEmployees: any[], allCourses: any[]) => {
  if (!allEmployees.length || !allCourses.length) return []
  
  const categories = [
    { name: 'Desenvolvimento Técnico', color: 'bg-brand-primary', textColor: 'text-brand-dark' },
    { name: 'Liderança & Gestão', color: 'bg-brand-blue', textColor: 'text-white' },
    { name: 'Soft Skills', color: 'bg-success', textColor: 'text-white' },
    { name: 'Certificações', color: 'bg-warning', textColor: 'text-white' }
  ]
  
  return categories.map((cat, index) => {
    const baseAllocation = 20000 + (index * 10000)
    const usagePercent = 0.6 + (Math.random() * 0.3) // 60-90% usage
    return {
      ...cat,
      budgetAllocated: baseAllocation,
      budgetUsed: Math.floor(baseAllocation * usagePercent),
      courses: Math.max(2, Math.floor(allCourses.length / categories.length)),
      employees: Math.max(4, Math.floor(allEmployees.length / categories.length) + Math.floor(Math.random() * 5))
    }
  })
}

// Generate upcoming courses from real course data
const generateUpcomingCourses = (realCourses: any[]) => {
  if (!realCourses.length) return []
  const categories = ['Desenvolvimento Técnico', 'Liderança & Gestão', 'Certificações', 'Soft Skills']
  
  return realCourses.slice(0, 4).map((course, index) => {
    const startDate = new Date()
    startDate.setDate(startDate.getDate() + (index * 7) + 14) // Start dates 2-5 weeks from now
    
    const maxParticipants = Math.floor(Math.random() * 10) + 6 // 6-15 participants
    const currentEnrollments = Math.floor(Math.random() * maxParticipants)
    const status = currentEnrollments >= maxParticipants ? 'full' : 'open'
    
    return {
      id: index + 1,
      name: course.title,
      category: categories[index % categories.length],
      cost: Math.floor(2000 + Math.random() * 6000), // Random cost 2000-8000
      duration: course.duration ? `${course.duration}h` : `${Math.floor(Math.random() * 30) + 10}h`,
      startDate: startDate.toISOString().split('T')[0],
      maxParticipants,
      currentEnrollments,
      status: status as 'open' | 'full'
    }
  })
}

function AvailableBudgetContent() {
  const { user } = useAuth()
  usePageTitle('Orçamento de Cursos')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [viewMode, setViewMode] = useState<'overview' | 'categories' | 'courses'>('overview')
  const [isLoading, setIsLoading] = useState(true)
  const [allEmployees, setAllEmployees] = useState<any[]>([])
  const [allCourses, setAllCourses] = useState<any[]>([])
  const [error, setError] = useState<string | null>(null)

  // Load async data
  useEffect(() => {
    const loadBudgetData = async () => {
      try {
        setIsLoading(true)
        setError(null)

        const [employees, courses] = await Promise.all([
          getAllMonumentaEmployees(),
          getAllCourses()
        ])

        setAllEmployees(employees)
        setAllCourses(courses)
      } catch (err) {
        console.error('Error loading budget data:', err)
        setError('Erro ao carregar dados de orçamento')
      } finally {
        setIsLoading(false)
      }
    }

    loadBudgetData()
  }, [])

  // Memoized derived data
  const courseCategories = useMemo(() => {
    if (!allEmployees.length || !allCourses.length) return []
    return generateCourseCategories(allEmployees, allCourses)
  }, [allEmployees, allCourses])

  const upcomingCourses = useMemo(() => {
    if (!allCourses.length) return []
    return generateUpcomingCourses(allCourses)
  }, [allCourses])
  
  // Check if user has permission (all roles except guest and padrao)
  const hasAccess = user && !['guest', 'padrao'].includes(user.role)

  // Show loading state
  if (isLoading) {
    return (
      
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto"></div>
            <p className="text-brand-gray-light font-body">Carregando dados de orçamento...</p>
          </div>
        </div>
      
    )
  }

  // Show error state
  if (error) {
    return (
      
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-brand-red/20 rounded-full flex items-center justify-center mx-auto">
              <AlertTriangle className="h-8 w-8 text-brand-red" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white mb-2">Erro ao Carregar Orçamento</h2>
              <p className="text-brand-gray-light">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="btn-primary mt-4"
              >
                Tentar Novamente
              </button>
            </div>
          </div>
        </div>
      
    )
  }

  if (!hasAccess) {
    return (
      
        <div className="min-h-screen bg-brand-dark flex items-center justify-center px-4">
          <div className="max-w-md w-full text-center">
            <div className="card border-brand-red/30 bg-brand-red/5">
              <AlertTriangle className="mx-auto h-16 w-16 text-brand-red mb-6" />
              <h2 className="font-display text-xl font-bold text-white mb-4">
                Acesso Restrito
              </h2>
              <p className="text-brand-gray mb-6">
                Esta página contém informações financeiras sobre orçamento de treinamentos. Apenas gestores e administradores podem acessá-la.
              </p>
              <p className="text-sm text-brand-gray bg-brand-dark-lighter/50 px-4 py-2 rounded-monumenta border border-brand-gray/20">
                Seu papel atual: <span className="text-brand-primary font-semibold">{user?.role || 'Não definido'}</span>
              </p>
            </div>
          </div>
        </div>
      
    )
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`
  }

  return (
    
      <div className="space-y-6 sm:space-y-8">
        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <button className="btn-secondary flex items-center gap-2">
            <Download size={16} />
            Exportar
          </button>
          <button className="btn-primary flex items-center gap-2">
            <Plus size={16} />
            Planejar Curso
          </button>
        </div>

        {/* Main Budget Metrics */}
        <div className="dashboard-grid lg:grid-cols-4">
          <MetricCard
            title="Orçamento Total"
            value={formatCurrency(BUDGET_DATA.totalBudget)}
            icon={DollarSign}
            trend="neutral"
          />
          <MetricCard
            title="Orçamento Utilizado"
            value={formatCurrency(BUDGET_DATA.usedBudget)}
            change={`${formatPercentage(BUDGET_DATA.budgetUtilization)} usado`}
            icon={TrendingUp}
            trend="up"
          />
          <MetricCard
            title="Orçamento Disponível"
            value={formatCurrency(BUDGET_DATA.availableBudget)}
            change={`${formatPercentage(100 - BUDGET_DATA.budgetUtilization)} restante`}
            icon={Target}
            trend="neutral"
          />
          <MetricCard
            title="Custo Médio/Curso"
            value={formatCurrency(BUDGET_DATA.averageCostPerCourse)}
            icon={BookOpen}
            trend="neutral"
          />
        </div>

        {/* Secondary Metrics */}
        <div className="dashboard-grid lg:grid-cols-4">
          <MetricCard
            title="Cursos Planejados"
            value={BUDGET_DATA.coursesPlanned}
            change="Este ano"
            icon={Calendar}
            trend="neutral"
          />
          <MetricCard
            title="Cursos Concluídos"
            value={BUDGET_DATA.coursesCompleted}
            change={`${((BUDGET_DATA.coursesCompleted / BUDGET_DATA.coursesPlanned) * 100).toFixed(0)}% completo`}
            icon={CheckCircle}
            trend="up"
          />
          <MetricCard
            title="Em Andamento"
            value={BUDGET_DATA.coursesInProgress}
            icon={Clock}
            trend="neutral"
          />
          <MetricCard
            title="Colaboradores Inscritos"
            value={BUDGET_DATA.employeesEnrolled}
            change="+8 este mês"
            icon={Users}
            trend="up"
          />
        </div>

        {/* Navigation Tabs */}
        <div className="bg-brand-dark-light/80 backdrop-blur-sm rounded-monumenta p-1 shadow-monumenta border border-gray-800/50">
          <div className="flex flex-wrap gap-1">
            {[
              { key: 'overview', label: 'Visão Geral', icon: PieChart },
              { key: 'categories', label: 'Por Categoria', icon: Filter },
              { key: 'courses', label: 'Próximos Cursos', icon: Calendar }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setViewMode(tab.key as any)}
                className={`flex items-center gap-2 px-6 py-3 rounded-monumenta font-display font-semibold transition-all duration-300 flex-1 justify-center ${
                  viewMode === tab.key
                    ? 'bg-brand-primary text-brand-dark shadow-monumenta-brand' 
                    : 'text-gray-300 hover:text-white hover:bg-brand-dark-lighter transform hover:scale-105'
                }`}
              >
                <tab.icon size={18} />
                <span className="hidden lg:inline">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {viewMode === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Budget Utilization Chart */}
            <div className="lg:col-span-2 card-hover">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-display text-xl font-semibold text-white">
                  Utilização do Orçamento
                </h3>
                <span className="text-sm text-brand-gray bg-brand-dark-lighter px-3 py-1 rounded-full">
                  {formatPercentage(BUDGET_DATA.budgetUtilization)} utilizado
                </span>
              </div>
              
              <div className="space-y-6">
                <div className="relative">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-white font-medium">Orçamento Anual</span>
                    <span className="text-brand-primary font-bold">{formatCurrency(BUDGET_DATA.totalBudget)}</span>
                  </div>
                  <div className="progress-bar h-4">
                    <div 
                      className="progress-fill h-4" 
                      style={{ width: `${BUDGET_DATA.budgetUtilization}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-sm text-brand-gray mt-2">
                    <span>Utilizado: {formatCurrency(BUDGET_DATA.usedBudget)}</span>
                    <span>Disponível: {formatCurrency(BUDGET_DATA.availableBudget)}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-brand-gray/20">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-success mb-1">{BUDGET_DATA.coursesCompleted}</div>
                    <div className="text-sm text-brand-gray">Cursos Concluídos</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-brand-primary mb-1">{BUDGET_DATA.employeesEnrolled}</div>
                    <div className="text-sm text-brand-gray">Colaboradores Beneficiados</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="space-y-6">
              <div className="card-hover">
                <h3 className="font-display text-lg font-semibold text-white mb-4">
                  Próximo Trimestre
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-brand-gray">Orçamento Previsto</span>
                    <span className="text-white font-semibold">{formatCurrency(BUDGET_DATA.nextQuarterBudget)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-brand-gray">Cursos Planejados</span>
                    <span className="text-brand-primary font-semibold">3 cursos</span>
                  </div>
                  <button className="btn-primary w-full mt-4">
                    Ver Planejamento
                  </button>
                </div>
              </div>

              <div className="card-interactive group">
                <div className="flex items-center gap-3 mb-3">
                  <AlertTriangle className="text-warning" size={20} />
                  <span className="font-display font-semibold text-white">Atenção</span>
                </div>
                <p className="text-brand-gray text-sm">
                  Restam {formatCurrency(BUDGET_DATA.availableBudget)} para os próximos 2 meses. 
                  Considere aprovar cursos pendentes em breve.
                </p>
              </div>
            </div>
          </div>
        )}

        {viewMode === 'categories' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {courseCategories.map((category, index) => {
                const utilizationRate = (category.budgetUsed / category.budgetAllocated) * 100
                
                return (
                  <div key={index} className="card-hover">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-display text-lg font-semibold text-white">
                        {category.name}
                      </h3>
                      <div className={`p-2 rounded-monumenta ${category.color}`}>
                        <BookOpen className={category.textColor} size={20} />
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-brand-gray">Utilização do Orçamento</span>
                          <span className="text-white font-medium">{formatPercentage(utilizationRate)}</span>
                        </div>
                        <div className="progress-bar">
                          <div 
                            className="progress-fill" 
                            style={{ width: `${Math.min(utilizationRate, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 pt-2 border-t border-brand-gray/20">
                        <div>
                          <div className="text-sm text-brand-gray">Orçamento</div>
                          <div className="font-semibold text-white">{formatCurrency(category.budgetAllocated)}</div>
                        </div>
                        <div>
                          <div className="text-sm text-brand-gray">Usado</div>
                          <div className="font-semibold text-brand-primary">{formatCurrency(category.budgetUsed)}</div>
                        </div>
                        <div>
                          <div className="text-sm text-brand-gray">Cursos</div>
                          <div className="font-semibold text-white">{category.courses}</div>
                        </div>
                        <div>
                          <div className="text-sm text-brand-gray">Colaboradores</div>
                          <div className="font-semibold text-white">{category.employees}</div>
                        </div>
                      </div>

                      <div className="pt-2">
                        <div className="text-sm text-brand-gray mb-1">Disponível</div>
                        <div className="font-semibold text-success text-lg">
                          {formatCurrency(category.budgetAllocated - category.budgetUsed)}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {viewMode === 'courses' && (
          <div className="space-y-6">
            <div className="card-hover">
              <h3 className="font-display text-xl font-semibold text-white mb-6">
                Próximos Cursos Planejados
              </h3>
              
              <div className="space-y-4">
                {upcomingCourses.map((course, index) => (
                  <div key={course.id} className="card-interactive group">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-display font-semibold text-white truncate">
                            {course.name}
                          </h4>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            course.status === 'open' 
                              ? 'bg-success/20 text-success border border-success/30' 
                              : 'bg-warning/20 text-warning border border-warning/30'
                          }`}>
                            {course.status === 'open' ? 'Vagas Disponíveis' : 'Lotado'}
                          </span>
                        </div>
                        <p className="text-sm text-brand-gray mb-3">{course.category}</p>
                        
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-brand-gray">Custo:</span>
                            <div className="font-semibold text-brand-primary">{formatCurrency(course.cost)}</div>
                          </div>
                          <div>
                            <span className="text-brand-gray">Duração:</span>
                            <div className="font-semibold text-white">{course.duration}</div>
                          </div>
                          <div>
                            <span className="text-brand-gray">Início:</span>
                            <div className="font-semibold text-white">
                              {new Date(course.startDate).toLocaleDateString('pt-BR')}
                            </div>
                          </div>
                          <div>
                            <span className="text-brand-gray">Inscritos:</span>
                            <div className="font-semibold text-white">
                              {course.currentEnrollments}/{course.maxParticipants}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="ml-4 flex gap-2">
                        <button className="btn-ghost btn-sm">
                          Ver Detalhes
                        </button>
                        {course.status === 'open' && (
                          <button className="btn-primary btn-sm">
                            Inscrever
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    
  )
}

export default function AvailableBudgetPage() {
  return (
    <ProtectedRoute>
      <AvailableBudgetContent />
    </ProtectedRoute>
  )
}
